import { AppData, Img, Lightning, Log, Utils } from "@lightningjs/sdk";
import { AppSetup } from "../../../app-setup";
import { Theme } from "../../../../ui-elements/info-ui";
import { Artwork, Icon } from "@lightningjs/ui-components";
import { Helper } from "../../../helper";
import VideoPreviewTexture from "../../../../ui-elements/video-preview-texture";
import { ItemNode } from "../ItemNode";

export default class ExpandableTileLandscape extends Lightning.Component<Lightning.Component.TemplateSpecLoose> {
    _theme: Theme = {};
    _imgSrc = ''
    _blockItem = false;
    _isCarouselRow = false;
    _videoItem: ItemNode | null = null;
    _rowType: string = '';
    
    // Global tracker to ensure only one preview plays at a time
    private static _activePreview: ExpandableTileLandscape | null = null;
    static override _template() {
        return {
            Wrapper: {
                zIndex: 7,
                x: 0,
                y: 10,
                w: (w) => w,
                h: (h) => h,
                collision:true,
                zIndex:10,
                Artwork: {},
                VideoPreview: {
                    type: VideoPreviewTexture,
                    x: 0,
                    y: 0,
                    w: (w) => w,
                    h: (h) => h,
                    visible: false
                },
                BlockItem: {},
                Stroke: {
                    x: -13,
                    y: -13,
                    w: (w) => w + 26,
                    h: (h) => h + 26,
                    rect: true,
                    color: 0x000000,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: 10,
                        stroke: 7,
                        strokeColor: 0x00000000
                    }
                },
            },
            Numbering: {}
        }
    }

    set imgSrc(url: string) {
        this._imgSrc = url
        const w = this.isNumberRow ? (this.w - 180) : this.w
        if (this._imgSrc !== '') {
            this.tag('Wrapper').tag('Artwork').patch({
                x: w/2,
                y: this.h/2,
                mount: 0.5,
                w: w,
                h: this.h,
                src: url
            })
        } else {
            //this.tag('Wrapper').patch({Artwork: {}})
        }
    }

    get imgSrc() {
        return this._imgSrc
    }

    set blockItem(value: boolean) {
        this._blockItem = value;
        //console.log(this._blockItem);
        this.updateBlockItem();
    }

    updateBlockItem() {
        const w = this.isNumberRow ? (this.w - 180) : this.w
        if (this._blockItem) {
            this.tag('Wrapper').patch({
                BlockItem: {
                    w: w,
                    h: this.h,
                    rect: true,
                    color: 0xAA222222,
                    Icon: {
                        x: w-10,
                        mountX: 1,
                        type: Icon,
                        y: 10,
                        w: 50,
                        h: 50,
                        icon: Utils.asset('/svg/ico-block.svg'),
                    }
                }
            })
        } else {
            this.tag('BlockItem').patch({})
        }
    }

    override _focus() {
        const { appConfig } = AppData as AppSetup;
        this.tag('Stroke').shader.strokeColor = this.isNumberRow ? null : this._theme.gridfocuscolor;
        if(this.isNumberRow){
            this.tag('Wrapper').smooth = { scale : 1.15 }
            this.tag('Numbering').patch({  alpha: 1 })
        }
        this.fireAncestors('$vodItemChanged');
        // Only start video preview if carousel autoplay is enabled
        if (appConfig?.enableMidCarouselAutoplay) {
            this._startVideoPreview();
        }
        if (appConfig?.enableMidCarousel && appConfig?.enableMidCarouselAutoplay && this._isCarouselRow && window.ftAutoplay) {
             window.ftAutoplay.on('onGridAutoplay', this._onAutoplayStart)
             window.ftAutoplay.on('onVideoAutoplay', this._onAutoplayStart)
        }
    }

    override _unfocus() {
        const { appConfig } = AppData as AppSetup;
        this.tag('Stroke').shader.strokeColor = 0x00000000
        if(this.isNumberRow){
            this.tag('Wrapper').smooth = { scale : 1 }
            this.tag('Numbering').patch({  alpha: 0.8 })
        }
        // Only stop video preview if carousel autoplay is enabled
        if (appConfig?.enableMidCarouselAutoplay) {
            this._stopVideoPreview();
        }
        if (window.ftAutoplay) {
             window.ftAutoplay.off('onGridAutoplay', this._onAutoplayStart)
             window.ftAutoplay.off('onVideoAutoplay', this._onAutoplayStart)
        }
    }

    private _onAutoplayStart = (item: any) => {
        if (ExpandableTileLandscape._activePreview === this) {
             this._startVideoPreview(true)
        }
    }

    override _init() {
        const { appState, appConfig } = AppData as AppSetup
        const app = appState?.getApp();
        this._theme = (app?.themesObj as Theme) || {};
        const appApiConfig = appState?.getAppApiConfig()
        this._theme.gridfocuscolor = `0xff${appApiConfig?.appLayoutConfig?.gridfocuscolor}` || '0xffffffff'
        if(this.isNumberRow) {
            this.tag('Wrapper').patch({
                x: this.itemIndex === 9 ? 280 : 140,
                w: this.w - 150
            })
            this.patch({
                Numbering: {
                alpha: 0.8,
                zIndex: 10,
                text: { 
                    text: this.itemIndex + 1,
                    fontFace: Helper.getFontFamily('Black'),
                    fontSize: 280,
                    offsetY: 240,
                    letterSpacing: -10
                }
            }
            })
        }
    }

    set isCarouselRow(isCarousel: boolean) {
        this._isCarouselRow = isCarousel
    }

    get isCarouselRow(): boolean {
        return this._isCarouselRow
    }

    set videoItem(item: ItemNode | null) {
        this._videoItem = item
    }

    get videoItem(): ItemNode | null {
        return this._videoItem
    }

    set rowType(rowType: string) {
        this._rowType = rowType
    }

    get rowType(): string {
        return this._rowType
    }

    private _startVideoPreview(force: boolean = false): void {
        const { appConfig } = AppData as AppSetup;
        Log.info('ExpandableTileLandscape: _startVideoPreview called', {
            isCarouselRow: this._isCarouselRow,
            hasVideoItem: !!this._videoItem,
            force: force,
            enableMidCarousel: appConfig?.enableMidCarousel,
            enableMidCarouselAutoplay: appConfig?.enableMidCarouselAutoplay
        })
        
        if (!appConfig?.enableMidCarousel || !this._isCarouselRow || !appConfig?.enableMidCarouselAutoplay) {
            return
        }
        
        if (!this._videoItem) {
            return
        }

        if (ExpandableTileLandscape._activePreview && 
            ExpandableTileLandscape._activePreview !== this) {
            ExpandableTileLandscape._activePreview._stopVideoPreview()
        }
        ExpandableTileLandscape._activePreview = this
        const videoPreview = this.tag('Wrapper.VideoPreview') as VideoPreviewTexture
        if (videoPreview) {
            videoPreview.visible = true
            Log.info('ExpandableTileLandscape: Calling startPreview() on VideoPreviewTexture')
            videoPreview.startPreview()
        } else {
            Log.error('ExpandableTileLandscape: VideoPreview component not found in template!')
        }
    }

    private _stopVideoPreview(): void {
        const { appConfig } = AppData as AppSetup;
        Log.info('ExpandableTileLandscape: _stopVideoPreview called', {
            isCarouselRow: this._isCarouselRow,
            enableMidCarousel: appConfig?.enableMidCarousel,
            enableMidCarouselAutoplay: appConfig?.enableMidCarouselAutoplay
        })
        if (!appConfig?.enableMidCarousel || !this._isCarouselRow || !appConfig?.enableMidCarouselAutoplay) {
            return
        }
        if (ExpandableTileLandscape._activePreview === this) {
            ExpandableTileLandscape._activePreview = null
        }
        const videoPreview = this.tag('Wrapper.VideoPreview') as VideoPreviewTexture
        if (videoPreview) {
            videoPreview.stopPreview()
            videoPreview.visible = false
        }
    }

    override _detach(): void {
        const { appConfig } = AppData as AppSetup;
        // Only stop video preview if carousel autoplay is enabled
        if (appConfig?.enableMidCarouselAutoplay) {
            this._stopVideoPreview()
        }
        super._detach()
    }

    _handleClick(target) {
        Log.info('Thumbnail Click: ', this)
        this.fireAncestors('$GridThumbClick', this)
        return false
    }

}
